/**
 * SingleDatePicker
 * Portal-rendered date picker with day / month / year navigation.
 *
 * Fixes the two long-standing issues of the per-screen copies:
 *  1. Year selection — tap the month/year label to drill up to a year grid.
 *  2. Viewport-aware placement — the panel flips above the trigger (or clamps)
 *     when there isn't room below, so the picker for the LAST field on a page
 *     is never cut off. Scrolling re-positions the panel instead of closing it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import ReactDOM from 'react-dom';
import Svg, { Path } from 'react-native-svg';
import { FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const BRAND_RED = '#a9211f';
const PANEL_W = 320;
const PANEL_H = 380; // upper-bound estimate used for flip/clamp decisions

const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const AR_DAYS_SHORT = ['أح', 'إث', 'ثل', 'أر', 'خم', 'جم', 'سب'];
const EN_DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const ChevronDownIcon = ({ size = 14, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1 4 21 4.9 21 6V20C21 21.1 20.1 22 19 22H5C3.9 22 3 21.1 3 20V6C3 4.9 3.9 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

interface DayCell {
  day: number;
  dateStr: string;
  isCurrentMonth: boolean;
}

function buildMonthCells(year: number, month: number): DayCell[] {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: DayCell[] = [];
  const prevDays = new Date(year, month, 0).getDate();
  for (let i = startWeekday - 1; i >= 0; i--) {
    const d = prevDays - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  return cells;
}

function computePanelPosition(layout: { x: number; y: number; width: number; height: number }) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  // Right-align the panel to the trigger (RTL forms), then clamp horizontally.
  let left = layout.x + layout.width - PANEL_W;
  left = Math.max(8, Math.min(left, vw - PANEL_W - 8));
  const below = layout.y + layout.height + 4;
  const above = layout.y - PANEL_H - 4;
  let top: number;
  if (below + PANEL_H <= vh - 8) {
    top = below; // fits below the trigger
  } else if (above >= 8) {
    top = above; // flip above
  } else {
    top = Math.max(8, vh - PANEL_H - 8); // clamp into view
  }
  return { top, left };
}

interface SingleDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
}

export default function SingleDatePicker({ value, onChange, minDate, maxDate, placeholder }: SingleDatePickerProps) {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<'days' | 'months' | 'years'>('days');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<View>(null);

  const initMonth = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(initMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState(initMonth.getMonth());
  const minYear = minDate ? new Date(minDate).getFullYear() : 1980;
  const minMonthIndex = minDate ? new Date(minDate).getMonth() : 0;
  const [yearRangeStart, setYearRangeStart] = useState(minYear);

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const reposition = useCallback(() => {
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setPos(computePanelPosition({ x, y, width: w, height: h }));
    });
  }, []);

  const handleOpen = () => {
    if (isOpen) { setIsOpen(false); return; }
    const d = value ? new Date(value) : new Date();
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setYearRangeStart(Math.max(minYear, d.getFullYear() - 6));
    setPickerMode('days');
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setPos(computePanelPosition({ x, y, width: w, height: h }));
      setIsOpen(true);
    });
  };

  // Reposition on scroll/resize (do NOT close — the old behaviour hid the panel
  // and made it impossible to bring back into view for the last field).
  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web') return;
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [isOpen, reposition]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const handleSelect = (dateStr: string) => { onChange(dateStr); setIsOpen(false); };

  const months = isRTL ? AR_MONTHS : EN_MONTHS;
  const dayHeaders = isRTL ? AR_DAYS_SHORT : EN_DAYS_SHORT;
  const cells = buildMonthCells(viewYear, viewMonth);
  const currentYear = new Date().getFullYear();
  const yearRange = Array.from({ length: 12 }, (_, i) => yearRangeStart + i);

  const displayValue = value
    ? (() => { const [y, m, d] = value.split('-'); return `${d}/${m}/${y}`; })()
    : '';

  const panelBg = isDark ? colors.card : '#FFFFFF';
  const panelBorder = colors.cardBorder;

  const renderHeader = () => {
    if (pickerMode === 'years') {
      return (
        <View style={dpStyles.monthNav}>
          <TouchableOpacity onPress={() => setYearRangeStart(yearRangeStart + 12)} style={dpStyles.navBtn}>
            <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
          </TouchableOpacity>
          <Text style={[dpStyles.monthLabel, { color: colors.textPrimary }]}>{yearRangeStart} - {yearRangeStart + 11}</Text>
          <TouchableOpacity onPress={() => { if (yearRangeStart - 12 >= minYear) setYearRangeStart(yearRangeStart - 12); }} style={dpStyles.navBtn} disabled={yearRangeStart <= minYear}>
            <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={yearRangeStart <= minYear ? '#CBD5E1' : colors.textPrimary} /></View>
          </TouchableOpacity>
        </View>
      );
    }
    if (pickerMode === 'months') {
      return (
        <View style={dpStyles.monthNav}>
          <TouchableOpacity onPress={() => setViewYear(viewYear + 1)} style={dpStyles.navBtn}>
            <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setPickerMode('years')}>
            <Text style={[dpStyles.monthLabel, { color: BRAND_RED }]}>{viewYear}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => { if (viewYear > minYear) setViewYear(viewYear - 1); }} style={dpStyles.navBtn} disabled={viewYear <= minYear}>
            <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={viewYear <= minYear ? '#CBD5E1' : colors.textPrimary} /></View>
          </TouchableOpacity>
        </View>
      );
    }
    return (
      <View style={dpStyles.monthNav}>
        <TouchableOpacity onPress={nextMonth} style={dpStyles.navBtn}>
          <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setPickerMode('months')}>
          <Text style={[dpStyles.monthLabel, { color: BRAND_RED }]}>{months[viewMonth]} {viewYear}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={prevMonth} style={dpStyles.navBtn}>
          <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
        </TouchableOpacity>
      </View>
    );
  };

  const renderBody = () => {
    if (pickerMode === 'years') {
      return (
        <View style={dpStyles.pickerGrid}>
          {yearRange.map(yr => {
            const isDisabled = yr < minYear;
            return (
              <TouchableOpacity key={yr} disabled={isDisabled} style={[dpStyles.pickerCell, yr === viewYear && { backgroundColor: BRAND_RED, borderRadius: 8 }, yr === currentYear && yr !== viewYear && { backgroundColor: isDark ? '#333' : '#F1F5F9', borderRadius: 8 }]} onPress={() => { setViewYear(yr); setPickerMode('months'); }}>
                <Text style={[dpStyles.pickerCellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, yr === viewYear && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{yr}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      );
    }
    if (pickerMode === 'months') {
      return (
        <View style={dpStyles.pickerGrid}>
          {months.map((m, idx) => {
            const isDisabled = viewYear === minYear && idx < minMonthIndex;
            return (
              <TouchableOpacity key={idx} disabled={isDisabled} style={[dpStyles.pickerCell, idx === viewMonth && { backgroundColor: BRAND_RED, borderRadius: 8 }]} onPress={() => { setViewMonth(idx); setPickerMode('days'); }}>
                <Text style={[dpStyles.pickerCellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, idx === viewMonth && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{m}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      );
    }
    return (
      <>
        <View style={dpStyles.dayHeaderRow}>
          {dayHeaders.map(h => <Text key={h} style={[dpStyles.dayHeaderText, { color: colors.textMuted }]}>{h}</Text>)}
        </View>
        <View style={dpStyles.grid}>
          {cells.map((cell, idx) => {
            const isSelected = cell.dateStr === value;
            const isToday = cell.dateStr === todayStr;
            const isDisabled = !cell.isCurrentMonth
              || (minDate ? cell.dateStr < minDate : false)
              || (maxDate ? cell.dateStr > maxDate : false);
            return (
              <TouchableOpacity key={idx} style={[dpStyles.cell, isSelected && { backgroundColor: BRAND_RED, borderRadius: 20 }, isToday && !isSelected && { backgroundColor: isDark ? '#333' : '#F1F5F9', borderRadius: 20 }]} onPress={() => !isDisabled && handleSelect(cell.dateStr)} disabled={isDisabled}>
                <Text style={[dpStyles.cellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, isSelected && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{cell.day}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </>
    );
  };

  const portalContent = isOpen && Platform.OS === 'web' ? ReactDOM.createPortal(
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 } as any} onClick={() => setIsOpen(false)} />
      <div style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 99999 } as any}>
        <View style={[dpStyles.panel, { backgroundColor: panelBg, borderColor: panelBorder }]}>
          {renderHeader()}
          {renderBody()}
        </View>
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <View ref={btnRef}>
      <TouchableOpacity onPress={handleOpen} style={[dpStyles.button, { backgroundColor: isDark ? colors.backgroundAlt : '#FFFFFF', borderColor: colors.cardBorder }]}>
        <CalendarIcon size={16} color={colors.textMuted} />
        <Text style={[dpStyles.buttonText, { color: value ? colors.textPrimary : colors.textMuted }]}>
          {displayValue || placeholder || (isRTL ? 'اختر التاريخ' : 'Select date')}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
}

const dpStyles = StyleSheet.create({
  button: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  buttonText: { fontSize: 14, fontFamily: FONTS.regular, flex: 1, textAlign: 'right' },
  panel: { width: PANEL_W, borderRadius: 12, borderWidth: 1, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 10 },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  navBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  monthLabel: { fontSize: 16, fontFamily: FONTS.medium },
  dayHeaderRow: { flexDirection: 'row', marginBottom: 8 },
  dayHeaderText: { flex: 1, textAlign: 'center', fontSize: 12, fontFamily: FONTS.medium },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: '14.28%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 8 },
  cellText: { fontSize: 14, fontFamily: FONTS.regular },
  pickerGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  pickerCell: { width: '30%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 12, marginHorizontal: '1.5%' as any },
  pickerCellText: { fontSize: 14, fontFamily: FONTS.regular },
});
